import "bootstrap/dist/css/bootstrap.css";
import React, { Suspense } from "react";
import ReactDOM from "react-dom";
import { BrowserRouter } from "react-router-dom";
import { unregister as unregisterServiceWorker } from "./registerServiceWorker";
import SpinloaderProvider from "./common/components/Spinloader";

import {
  ThemeProvider,
  StyledEngineProvider,
  createTheme,
} from "@mui/material/styles";
import { SnackbarProvider } from "notistack";

import "./global-styles";

const App = React.lazy(() => import("./App"));

const baseUrl = document.getElementsByTagName("base")[0].getAttribute("href");
const rootElement = document.getElementById("root");

const ASSET_MANIFEST_MAIN_JS_KEY = "hsa.assetManifest.mainJs";

async function reloadIfNewFrontendBuild() {
  if (process.env.NODE_ENV !== "production") {
    return;
  }

  try {
    const response = await fetch(`${baseUrl}asset-manifest.json`, {
      cache: "no-store",
    });

    if (!response.ok) {
      return;
    }

    const manifest = await response.json();
    const currentMainJs = manifest?.files?.["main.js"];
    if (!currentMainJs) {
      return;
    }

    const previousMainJs = localStorage.getItem(ASSET_MANIFEST_MAIN_JS_KEY);

    if (previousMainJs && previousMainJs !== currentMainJs) {
      localStorage.setItem(ASSET_MANIFEST_MAIN_JS_KEY, currentMainJs);
      window.location.reload();
      return;
    }

    if (!previousMainJs) {
      localStorage.setItem(ASSET_MANIFEST_MAIN_JS_KEY, currentMainJs);
    }
  } catch {
    // Best-effort: if anything goes wrong, avoid blocking app startup.
  }
}

function scheduleReloadIfNewFrontendBuild() {
  if (typeof window.requestIdleCallback === "function") {
    window.requestIdleCallback(() => {
      reloadIfNewFrontendBuild();
    });
    return;
  }

  window.setTimeout(() => {
    reloadIfNewFrontendBuild();
  }, 0);
}

export const theme = createTheme({
  palette: {
    selected: {
      main: "rgba(0, 0, 0, 0.2)",
    },
    primary: {
      // light: will be calculated from palette.primary.main,
      main: "#0673C1",
      inactive: "rgba(0, 0, 0, 0.54)",
      // dark: will be calculated from palette.primary.main,
      // contrastText: will be calculated to contrast with palette.primary.main
    },
    secondary: {
      // light: will be calculated from palette.primary.main,
      main: "#182A42",
      // dark: will be calculated from palette.primary.main,
      // contrastText: will be calculated to contrast with palette.primary.main
    },
    icon: {
      main: "rgba(0, 0, 0, 0.54)",
      disabled: "rgba(0, 0, 0, 0.26)",
    },
    default: {
      main: "#ffffff",
    },
    error: {
      main: "#d84646",
    },
    success: {
      main: "#4e9a51",
    },
    warning: {
      main: "#f68a1c",
    },
  },
  typography: {
    useNextVariants: true,
  },
});

ReactDOM.render(
  <BrowserRouter basename={baseUrl}>
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <SpinloaderProvider>
          <SnackbarProvider maxSnack={3}>
            <Suspense fallback={null}>
              <App />
            </Suspense>
          </SnackbarProvider>
        </SpinloaderProvider>
      </ThemeProvider>
    </StyledEngineProvider>
  </BrowserRouter>,
  rootElement,
);

scheduleReloadIfNewFrontendBuild();

unregisterServiceWorker();
